import {
  faArrowDownToSquare,
  faArrowUpFromSquare,
  faTrashCan,
} from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, ButtonGroup } from "@progress/kendo-react-buttons";
import { Grid, GridColumn, GridToolbar } from "@progress/kendo-react-grid";
import { Popup } from "@progress/kendo-react-popup";
import React from "react";
import { listTypeDatasets } from "../../../Common/Store/CommonSignals.jsx";
import ColorChangingIcon from "../../../Common/UI/ColorChangingIcon";
import FilterValuesWithoutParentDialog from "../../../Notification/FilterValuesWithoutParentDialog.jsx";
import InvalidFilterValuesDialog from "../../../Notification/InvalidFilterValuesDialog.jsx";
import InvalidListFormatDialog from "../../../Notification/InvalidListFormatDialog.jsx";
import { addRecord, handleMoveDown, handleMoveUp } from "../../DatasetUtils.js";
import {
  datasetData,
  parentDatasetName,
  showFileUpload,
  showParentDatasetSelection,
  skip,
} from "../../NewDatasetSignals.jsx";
import {
  exportToCsvFile,
  hasSecondColumnWithKeyword,
  transformCsvRows,
  validateCsvFormat,
} from "../CsvUtilities/CsvHelper.js";
import { ReadCSV } from "../CsvUtilities/ReadCSV.js";
import { DatasetCell, DeleteCell, ValueCell } from "./CustomCells.js";
import {
  invalidFilterValues,
  pendingCsvData,
  showFilterValuesWithoutParentDialog,
  showInvalidListFormatDialog,
} from "./DatasetListSignals.jsx";

export const ListGrid = ({ onChange }) => {
  const anchor = React.useRef(null);

  const closeInvalidFilterValuesDialog = () => {
    invalidFilterValues.value = [];
    showFileUpload.value = false;
  };

  const confirmImportWithoutParent = () => {
    // Remove filterValues from pending data and import
    const cleanedData = pendingCsvData.value.map((row) => {
      const { filterValue, ...rest } = row;
      return rest;
    });
    updateListData(cleanedData);
    pendingCsvData.value = null;
    showFilterValuesWithoutParentDialog.value = false;
    showFileUpload.value = false;
  };

  const cancelImportWithoutParent = () => {
    pendingCsvData.value = null;
    showFilterValuesWithoutParentDialog.value = false;
    showFileUpload.value = false;
  };

  const updateListData = (newListData) => {
    onChange(newListData);
  };

  const itemChange = (event) => {
    const inEditID = event.dataItem.id;
    const field = event.field || "";
    const newData = datasetData.value.values.map((item) =>
      item.index === inEditID
        ? {
            ...item,
            [field]: event.value,
          }
        : item
    );
    updateListData(newData);
  };

  const uploadCSV = () => {
    showFileUpload.value = !showFileUpload.value;
  };

  const exportToCSV = () => {
    const hasFilterValue = datasetData.value.parentDataset;
    const headers = hasFilterValue ? ["Value", "Filter Value"] : ["Value"];

    exportToCsvFile({
      values: datasetData.value.values,
      datasetName: datasetData.value.datasetName,
      headers,
      hasSecondColumn: hasFilterValue,
      secondColumnKey: "filterValue",
    });
  };

  const handleFileChange = async (e) => {
    if (e.target.files?.[0]) {
      const csvResult = await ReadCSV(e.target.files[0]);

      // Validate CSV format for List datasets
      const validation = validateCsvFormat(csvResult, "value");
      if (!validation.isValid) {
        showFileUpload.value = false;
        showInvalidListFormatDialog.value = true;
        return;
      }

      // Transform rows to dataset format
      const hasFilterValueColumn = hasSecondColumnWithKeyword(csvResult.headers, "filter");
      const csvData = transformCsvRows(csvResult.rows, hasFilterValueColumn, "filterValue");

      // Check if CSV has filterValues but dataset has no parent dataset
      const hasFilterValuesInCsv = csvData.some((row) => row.filterValue);
      if (hasFilterValuesInCsv && !datasetData.value.parentDataset) {
        pendingCsvData.value = csvData;
        showFileUpload.value = false;
        showFilterValuesWithoutParentDialog.value = true;
        return;
      }

      // Validate filterValues against parent dataset if exists
      if (datasetData.value.parentDataset) {
        const parentDataset = listTypeDatasets.value?.find(
          (dataset) => dataset.id === datasetData.value.parentDataset
        );
        const validFilterValues = parentDataset?.values?.map((row) => row.value) || [];

        const invalidRows = csvData.filter(
          (row) => row.filterValue && !validFilterValues.includes(row.filterValue)
        );

        if (invalidRows.length > 0) {
          const invalidValues = [...new Set(invalidRows.map((row) => row.filterValue))];
          invalidFilterValues.value = invalidValues;
          showFileUpload.value = false;
          return;
        }
      }

      updateListData(csvData);
    }
    showFileUpload.value = false;
  };

  const closeInvalidListFormatDialog = () => {
    showInvalidListFormatDialog.value = false;
  };

  const showParentDatasetPopup = () => {
    showParentDatasetSelection.value = !showParentDatasetSelection.value;
  };

  const handleParentDatasetChange = (e) => {
    datasetData.value = { ...datasetData.value, parentDataset: e.target.value };
    showParentDatasetSelection.value = false;
  };

  const removeParentDataset = () => {
    datasetData.value = { ...datasetData.value, parentDataset: null };
  };

  const pageChange = (event) => {
    skip.value = event.page.skip;
  };
  return (
    <>
      {showInvalidListFormatDialog.value && (
        <InvalidListFormatDialog handleClose={closeInvalidListFormatDialog} />
      )}
      {invalidFilterValues.value.length > 0 && (
        <InvalidFilterValuesDialog
          invalidValues={invalidFilterValues.value}
          handleClose={closeInvalidFilterValuesDialog}
        />
      )}
      {showFilterValuesWithoutParentDialog.value && (
        <FilterValuesWithoutParentDialog
          handleConfirm={confirmImportWithoutParent}
          handleCancel={cancelImportWithoutParent}
        />
      )}
      <Grid
        data={datasetData.value.values}
        style={{ height: "750px" }}
        skip={skip.value}
        total={datasetData.value.values?.length}
        pageSize={20}
        rowHeight={40}
        onItemChange={(e) => itemChange(e)}
        scrollable="virtual"
        onPageChange={pageChange}
      >
        <GridToolbar>
          <div style={{ display: "flex", alignItems: "center", gap: "5px", flexWrap: "wrap" }}>
            <button
              title="Add new"
              name="btnDatasetListAddRow"
              type="button"
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
              onClick={() =>
                addRecord(datasetData.value.values, updateListData, {
                  value: "",
                  filterValue: "",
                })
              }
            >
              Add
            </button>
            <button
              title="Import Data"
              name="btnDatasetListImportData"
              type="button"
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary import-button"
              onClick={uploadCSV}
              ref={anchor}
            >
              {showFileUpload.value ? "Cancel" : "Import Data"}
            </button>
            <button
              title="Export Data"
              name="btnDatasetListExportData"
              type="button"
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
              onClick={exportToCSV}
            >
              Export Data
            </button>
            <Popup
              anchor={anchor.current}
              show={showFileUpload.value}
              popupClass={"popup-content"}
            >
              <div className="upload-section">
                <h3>Upload CSV File:</h3>
                <input
                  className="chooseFile"
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                />
              </div>
            </Popup>

            {!datasetData.value.parentDataset ? (
              <button
                title="Parent Dataset"
                name="btnDatasetListAddParentDataset"
                type="button"
                className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary import-button"
                onClick={showParentDatasetPopup}
                ref={anchor}
              >
                {showParentDatasetSelection.value ? "Close" : "Add Parent Dataset"}
              </button>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <strong className="child-table-names">
                  Parent Dataset: {parentDatasetName.value}
                </strong>
                <ColorChangingIcon
                  icon={faTrashCan}
                  initialPrimaryColor="#6e6e6e"
                  initialSecondaryColor="#919191"
                  padding="3px 0 0 0"
                  margin="0"
                  onClick={removeParentDataset}
                />
              </div>
            )}

            <Popup
              anchor={anchor.current}
              show={showParentDatasetSelection.value}
              popupClass={"popup-content"}
            >
              <div className="child-select-section">
                <select
                  className="tableSelect"
                  value={datasetData.value.parentDataset}
                  onChange={handleParentDatasetChange}
                >
                  <option value="">Select a Parent Dataset</option>
                  {listTypeDatasets.value.map((listGridDataset) => (
                    <option
                      key={listGridDataset.id}
                      value={listGridDataset.id}
                    >
                      {listGridDataset.datasetName}
                    </option>
                  ))}
                </select>
              </div>
            </Popup>
          </div>
        </GridToolbar>
        <GridColumn
          width={80}
          title={null}
          cells={{
            data: renderMoveCell,
          }}
        />
        <GridColumn
          width="350px"
          field="value"
          title="Value"
          cells={{
            data: renderValueCell,
          }}
        />
        {datasetData.value.parentDataset && (
          <GridColumn
            field="filterValue"
            title="Filter Value"
            cells={{
              data: renderFilterCell,
            }}
          />
        )}
        <GridColumn
          field="delete"
          title="Delete"
          cells={{
            data: renderDeleteCell,
          }}
        />
      </Grid>
    </>
  );

  function renderMoveCell(props) {
    return (
      <td>
        <ButtonGroup>
          <Button
            className="!k-px-2"
            onClick={() => handleMoveUp(props, datasetData.value.values, updateListData)}
          >
            <div
              className="align-columns"
              style={{ margin: 0, paddingInline: 0, paddingBlock: 0 }}
            >
              <FontAwesomeIcon
                icon={faArrowUpFromSquare}
                style={{
                  "--fa-primary-color": "#0078D4",
                  "--fa-secondary-color": "#919191",
                  "--fa-primary-opacity": "1",
                  "--fa-secondary-opacity": "1",
                  fontSize: 14,
                }}
              />
            </div>
          </Button>
          <Button
            className="!k-px-2"
            onClick={() => handleMoveDown(props, datasetData.value.values, updateListData)}
          >
            <div
              className="align-columns"
              style={{ margin: 0, paddingInline: 0, paddingBlock: 0 }}
            >
              <FontAwesomeIcon
                icon={faArrowDownToSquare}
                style={{
                  "--fa-primary-color": "#0078D4",
                  "--fa-secondary-color": "#919191",
                  "--fa-primary-opacity": "1",
                  "--fa-secondary-opacity": "1",
                  fontSize: 14,
                }}
              />
            </div>
          </Button>
        </ButtonGroup>
      </td>
    );
  }

  function renderDeleteCell(props) {
    return (
      <DeleteCell
        {...props}
        data={datasetData.value.values}
        setData={updateListData}
      />
    );
  }

  function renderFilterCell(props) {
    const parentDataset = listTypeDatasets.value?.find(
      (dataset) => dataset?.datasetName === parentDatasetName.value
    );
    const options = parentDataset?.values?.map((row) => row.value) || [];

    return (
      <DatasetCell
        {...props}
        options={options}
        data={datasetData.value.values}
        setData={updateListData}
      />
    );
  }

  function renderValueCell(props) {
    return (
      <ValueCell
        {...props}
        data={datasetData.value.values}
        setData={updateListData}
      />
    );
  }
};
